import type { DistributionConfig } from '../../models/simulation';

export type Rng = () => number;

export const createRng = (seed = Date.now() % 2147483647): Rng => {
    let state = (seed >>> 0) + 0x6d2b79f5;
    return () => {
        state |= 0;
        state = (state + 0x6d2b79f5) | 0;
        let t = Math.imul(state ^ (state >>> 15), 1 | state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

const clamp = (value: number, min?: number, max?: number): number => {
    let result = value;
    if (Number.isFinite(min)) {
        result = Math.max(result, min!);
    }
    if (Number.isFinite(max)) {
        result = Math.min(result, max!);
    }
    return result;
};

const sampleFixed = (value: number, trials: number): Float64Array => {
    const samples = new Float64Array(trials);
    samples.fill(value);
    return samples;
};

const sampleUniform = (min: number, max: number, trials: number, rng: Rng): Float64Array => {
    const samples = new Float64Array(trials);
    const span = max - min;
    for (let i = 0; i < trials; i += 1) {
        samples[i] = min + rng() * span;
    }
    return samples;
};

const sampleNormal = (
    mean: number,
    std: number,
    trials: number,
    rng: Rng,
    min?: number,
    max?: number
): Float64Array => {
    const samples = new Float64Array(trials);
    for (let i = 0; i < trials; i += 2) {
        const u1 = rng() || Number.MIN_VALUE;
        const u2 = rng() || Number.MIN_VALUE;
        const radius = Math.sqrt(-2.0 * Math.log(u1));
        const theta = 2.0 * Math.PI * u2;
        const z0 = radius * Math.cos(theta);
        const z1 = radius * Math.sin(theta);
        samples[i] = clamp(mean + z0 * std, min, max);
        if (i + 1 < trials) {
            samples[i + 1] = clamp(mean + z1 * std, min, max);
        }
    }
    return samples;
};

const sampleTriangular = (
    min: number,
    mode: number,
    max: number,
    trials: number,
    rng: Rng
): Float64Array => {
    const samples = new Float64Array(trials);
    const f = (mode - min) / (max - min);
    for (let i = 0; i < trials; i += 1) {
        const u = rng();
        if (u < f) {
            samples[i] = min + Math.sqrt(u * (max - min) * (mode - min));
        } else {
            samples[i] = max - Math.sqrt((1 - u) * (max - min) * (max - mode));
        }
    }
    return samples;
};

export const sampleDistribution = (
    config: DistributionConfig,
    trials: number,
    rng: Rng
): Float64Array => {
    switch (config.kind) {
        case 'fixed':
            return sampleFixed(config.value, trials);
        case 'uniform':
            return sampleUniform(config.min, config.max, trials, rng);
        case 'normal':
            return sampleNormal(config.mean, config.std, trials, rng, config.min, config.max);
        case 'triangular':
            return sampleTriangular(config.min, config.mode, config.max, trials, rng);
        default:
            throw new Error(`Unsupported distribution kind: ${(config as { kind: string }).kind}`);
    }
};

